const pageZoom = () => {
  const zoom = Number.parseFloat(
    window.getComputedStyle(document.documentElement).zoom
  );
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
};

const motionRect = rect => {
  const zoom = pageZoom();
  return {
    left: rect.left / zoom,
    top: rect.top / zoom,
    width: rect.width / zoom,
    height: rect.height / zoom,
  };
};

export const createContactAvatarMotion = origin => {
  let observer;
  let image;
  let overlay;
  let clone;
  let opening;
  let openingPlayed = false;
  let closing = false;

  const animate = async (fromRect, toRect, isClosing) => {
    if (!image || !overlay || !fromRect || !toRect) return;

    const from = motionRect(fromRect);
    const to = motionRect(toRect);
    if (!from.width || !from.height || !to.width || !to.height) return;

    clone = document.createElement('img');
    clone.src = image.currentSrc || image.src;
    clone.alt = '';
    clone.draggable = false;
    Object.assign(clone.style, {
      position: 'fixed',
      left: `${to.left}px`,
      top: `${to.top}px`,
      width: `${to.width}px`,
      height: `${to.height}px`,
      objectFit: 'cover',
      borderRadius: isClosing ? '50%' : '4px',
      pointerEvents: 'none',
      transformOrigin: 'top left',
      zIndex: '2147483646',
    });
    document.body.appendChild(clone);
    image.classList.add('invisible');

    const startTransform = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})`;
    const duration = 280;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const imageAnimation = clone.animate(
      [
        { transform: startTransform, borderRadius: isClosing ? '4px' : '50%' },
        { transform: 'none', borderRadius: isClosing ? '50%' : '4px' },
      ],
      { duration, easing, fill: 'forwards' }
    );
    const backdropAnimation = overlay.animate(
      [{ opacity: isClosing ? 1 : 0 }, { opacity: isClosing ? 0 : 1 }],
      { duration, easing, fill: 'forwards' }
    );
    await Promise.allSettled([
      imageAnimation.finished,
      backdropAnimation.finished,
    ]);
    clone.remove();
    clone = null;
    overlay.style.opacity = isClosing ? '0' : '1';
    backdropAnimation.cancel();
    image.classList.remove('invisible');
  };

  const open = () => {
    if (openingPlayed || !image.naturalWidth) return;
    openingPlayed = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    opening = new Promise(resolve => {
      requestAnimationFrame(() => {
        resolve(animate(origin, image.getBoundingClientRect(), false));
      });
    });
  };

  const mount = () => {
    document.body.classList.add('acl-contact-avatar-viewer-open');
    const findImage = () => {
      image = document.querySelector('.gallery-viewer-avatar-image');
      if (!image) return;
      overlay = image.closest('.gallery-viewer-overlay');
      if (!overlay) return;

      observer.disconnect();
      overlay.classList.add('!bg-white/90');
      overlay
        .querySelector('.gallery-viewer-stage')
        ?.classList.add('!bg-transparent');
      image.addEventListener('load', open, { once: true });
      if (image.complete) open();
    };

    observer = new MutationObserver(findImage);
    observer.observe(document.body, { childList: true, subtree: true });
    findImage();
  };

  const close = async () => {
    if (closing) return false;
    closing = true;
    if (opening) await opening;
    if (
      image?.naturalWidth &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      const avatar = document.querySelector(
        '.contact-details-info__avatar-button'
      );
      await animate(
        image.getBoundingClientRect(),
        avatar?.getBoundingClientRect() || origin,
        true
      );
    }
    return true;
  };

  const dispose = () => {
    document.body.classList.remove('acl-contact-avatar-viewer-open');
    observer?.disconnect();
    image?.removeEventListener('load', open);
    clone?.remove();
  };

  return { mount, close, dispose };
};
